import Cookies from 'js-cookie';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  Observable,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { API_URL } from '../constants';
import emitter, { LOADER_EVENTS } from '@/utils/loaderEvents';
import { useMemo } from 'react';

// HTTP Link
const httpLink = createHttpLink({ uri: API_URL });

// Auth Link
const authLink = setContext((_, { headers }) => {
  const token = Cookies.get('authToken');
  
  return {
    headers: {
      ...headers,
      ...(token && { authorization: `Bearer ${token}` }),
    },
  };
});

// Error Link - using onError for better error handling
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // Handle GraphQL errors
  if (graphQLErrors) {
    console.log("GraphQL Errors:", graphQLErrors);
    
    const hasAuthError = graphQLErrors.some((error) => {
      const code = error.extensions?.code?.toLowerCase() || '';
      const message = error.message?.toLowerCase() || '';
      
      // Check for various authentication/authorization errors
      return (
        code.includes('unauthenticated') ||
        code.includes('unauthorized') ||
        code.includes('invalid') ||
        code.includes('forbidden') ||
        message.includes('unauthenticated') ||
        message.includes('unauthorized') ||
        message.includes('not authenticated') ||
        message.includes('not authorized')
      );
    });

    if (hasAuthError) {
      console.log("Auth error detected, redirecting to login...");
      Cookies.remove('authToken');
      Cookies.remove('id');
      
      // Prevent infinite loops by checking current location
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
  }

  // Handle network errors (including parse errors)
  if (networkError) {
    console.log("Network Error:", networkError);
    
    // Check if it's an auth-related network error (401, 403)
    if ('statusCode' in networkError) {
      const statusCode = networkError.statusCode;
      
      if (statusCode === 401 || statusCode === 403) {
        console.log("Auth network error (status: " + statusCode + "), redirecting to login...");
        Cookies.remove('authToken');
        Cookies.remove('id');
        
        // Prevent infinite loops
        if (window.location.pathname !== '/login') {
          window.location.replace('/login');
        }
      }
    }
    
    // Handle parse errors or malformed responses
    if (networkError.message?.includes('JSON') || networkError.message?.includes('Unexpected token')) {
      console.log("Parse error detected, clearing auth and redirecting...");
      Cookies.remove('authToken');
      Cookies.remove('id');
      
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
  }
});

// Loading Link
const EXCLUDED_OPERATIONS = ['Product_categories']; 

const loadingLink = new ApolloLink((operation, forward) => {
  const shouldEmitLoader = !EXCLUDED_OPERATIONS.includes(operation.operationName);
  
  if (shouldEmitLoader) {
    setTimeout(() => {
      emitter.emit(LOADER_EVENTS.START);
    }, 0);
  }
  
  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => observer.next(result),
      error: (error) => {
        console.log("Operation error:", error);
        observer.error(error);
      },
      complete: () => observer.complete(),
    });
    
    return () => {
      if (shouldEmitLoader) {
        setTimeout(() => {
          emitter.emit(LOADER_EVENTS.STOP);
        }, 0);
      }
      subscription.unsubscribe();
    };
  });
});

// Create Apollo Client
export default function createApolloClient() {
  return new ApolloClient({
    link: ApolloLink.from([loadingLink, errorLink, authLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { 
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all', // Return both data and errors
      },
      query: {
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });
}

// Hook to use Apollo Client
export function useApollo() {
  const client = useMemo(() => createApolloClient(), []);
  return client;
}